import React, { useState } from 'react';
import Button from 'components/Button';
import { ApprovalSummaryData } from '..';
import { useDisclosure } from 'further-ui/hooks';
import { CalendarDay, pluralize } from 'further-ui/utils';
import Page from 'components/new/Page';
import { Row } from 'further-ui/components/Layout';
import Approval from './Approval';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import api from 'lib/trpcClient';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { ListLpFeesCategory } from 'further-types/lp/lp-fee';
import { useLayoutContext } from 'contexts/LayoutContext';
import useVehicle from 'hooks/lp/data/vehicle/useVehicle';
import { LpFeeCommitmentStatus } from 'further-types/lp/lp-fee-template';
import { sumBy } from 'lodash';
import Processing from './Processing';
import { getQueryKey } from '@trpc/react-query';

const confirmDialogContent = {
  [ListLpFeesCategory.DueUnapprovedFees]: {
    title: 'Confirm Approval',
    message:
      'Are you sure? Fees should only be approved when all quarterly actions have been processed, as actions within the quarter may affect the fee total.',
  },
  [ListLpFeesCategory.FutureUnapprovedFees]: {
    title: 'Confirm Approval',
    message:
      'Are you sure you want to approve these now? These fees are not yet due and other transactions between now and the due date may affect the fee amount.',
  },
};

const ApproveManagementFeesSummary: React.FC<{
  approvalSummaryData: ApprovalSummaryData;
  onBack: () => void;
  setApprovalSummaryData: (data: ApprovalSummaryData) => void;
}> = ({ approvalSummaryData, setApprovalSummaryData, onBack }) => {
  const { selectedVehicleId } = useLayoutContext();
  const { vehicle } = useVehicle(selectedVehicleId);
  const { fullFeeData, feeType, defaultTransactions } = approvalSummaryData;
  const [approvalComplete, setApprovalComplete] = useState(false);
  const confirmDialog = useDisclosure();
  const mutationOpts = useHandleTrpcMutation();
  const queryKey = getQueryKey(api.lpFee.list);
  const approveFeesMutation = api.lpFee.approve.useMutation(
    mutationOpts({
      onSuccess: () => {
        setApprovalComplete(true);
      },
      invalidations: [queryKey],
    }),
  );

  const { insideCommitmentFees, outsideCommitmentFees } = fullFeeData.reduce<{
    insideCommitmentFees: ApprovalSummaryData['fullFeeData'];
    outsideCommitmentFees: ApprovalSummaryData['fullFeeData'];
  }>(
    (acc, feeData) => {
      if (
        feeData.insideOutsideCommitment ===
        LpFeeCommitmentStatus.InsideCommitment
      ) {
        acc.insideCommitmentFees.push(feeData);
      } else {
        acc.outsideCommitmentFees.push(feeData);
      }
      return acc;
    },
    {
      insideCommitmentFees: [],
      outsideCommitmentFees: [],
    },
  );

  const [insideCommitmentTransactions, setInsideCommitmentTransactions] =
    useState<ApprovalSummaryData['defaultTransactions']['insideCommitment']>(
      insideCommitmentFees.length > 0
        ? defaultTransactions.insideCommitment.map((transaction) => ({
            ...transaction,
            amountIncVatTcy: sumBy(insideCommitmentFees, 'feeIncTaxLcy'),
            amountExVatTcy: sumBy(insideCommitmentFees, 'feeExTaxLcy'),
          }))
        : [],
    );

  const [outsideCommitmentTransactions, setOutsideCommitmentTransactions] =
    useState<ApprovalSummaryData['defaultTransactions']['outsideCommitment']>(
      outsideCommitmentFees.length > 0
        ? defaultTransactions.outsideCommitment.map((transaction) => ({
            ...transaction,
            amountIncVatTcy: sumBy(outsideCommitmentFees, 'feeIncTaxLcy'),
            amountExVatTcy: sumBy(outsideCommitmentFees, 'feeExTaxLcy'),
          }))
        : [],
    );

  const numberOfFees = fullFeeData.length;
  const valueOfFees = sumBy(fullFeeData, 'feeIncTaxLcy');

  const handleApproveFees = () => {
    confirmDialog.onClose();
    if (!vehicle) return;
    approveFeesMutation.mutate({
      vehicleId: vehicle._id.toString(),
      insideCommitment: {
        transactions: insideCommitmentTransactions,
        lpFees: insideCommitmentFees,
      },
      outsideCommitment: {
        transactions: outsideCommitmentTransactions,
        lpFees: outsideCommitmentFees,
      },
    });
  };

  const handleBackClick = () => {
    if (approvalComplete) {
      setApprovalSummaryData({
        id: '',
        date: new CalendarDay(new Date()),
        fullFeeData: [],
        defaultTransactions: {
          insideCommitment: [],
          outsideCommitment: [],
        },
        feeType,
      });
    }
    onBack();
  };

  return (
    <Page
      goBackAction={handleBackClick}
      tabTitle="Management fees: review and approve"
      heading="Review and approve"
      subheading={`You are processing ${numberOfFees} ${pluralize(
        'fee',
        numberOfFees,
      )}.`}
      withHeadingDivider
      actionBar={
        <Row
          fullWidth
          justify={approvalComplete ? 'flex-end' : 'space-between'}
        >
          <Button
            onClick={handleBackClick}
            variant={approvalComplete ? 'contained' : 'outlined'}
            size="large"
          >
            Back
          </Button>
          {approvalComplete ? null : (
            <Button
              onClick={confirmDialog.onOpen}
              variant="contained"
              size="large"
              loading={approveFeesMutation.isPending}
            >
              {approveFeesMutation.isPending ? 'Approving' : 'Approve'}
            </Button>
          )}
        </Row>
      }
    >
      {approveFeesMutation.isPending || approvalComplete ? (
        <Processing
          loading={approveFeesMutation.isPending}
          currencyLcy={vehicle?.baseCurrency}
          feeValue={valueOfFees}
          numberOfFees={numberOfFees}
        />
      ) : (
        <Approval
          approvalSummaryData={approvalSummaryData}
          outsideCommitmentTransactions={outsideCommitmentTransactions}
          insideCommitmentTransactions={insideCommitmentTransactions}
          setInsideCommitmentTransactions={setInsideCommitmentTransactions}
          setOutsideCommitmentTransactions={setOutsideCommitmentTransactions}
        />
      )}
      {feeType ? (
        <ConfirmDialog
          maxWidth="sm"
          open={confirmDialog.isOpen}
          onClose={confirmDialog.onClose}
          actions={{
            confirm: {
              label: 'Confirm',
              onClick: handleApproveFees,
            },
          }}
          title={confirmDialogContent[feeType].title}
          content={confirmDialogContent[feeType].message}
        />
      ) : null}
    </Page>
  );
};

export default ApproveManagementFeesSummary;
